import React from 'react';
import { Calendar, Heart, Bot, Route, Book, Users, Star, Shield } from 'lucide-react';
import Carousel from './Carousel';

const Features: React.FC = () => {
  const mainFeatures = [
    {
      icon: Calendar,
      title: 'Santo do Dia',
      description: 'Descubra diariamente a história e os ensinamentos dos santos da Igreja Católica.',
      color: 'from-gold-500 to-gold-600'
    },
    {
      icon: Heart,
      title: 'Rezar o Terço',
      description: 'Guia completo para oração do Santo Rosário com meditações dos mistérios.',
      color: 'from-brown-500 to-brown-600'
    },
    {
      icon: Bot,
      title: 'IA Devocional',
      description: 'Assistente espiritual inteligente para suas dúvidas sobre fé e doutrina.',
      color: 'from-gold-600 to-brown-500'
    },
    {
      icon: Route,
      title: 'Jornada da Bondade',
      description: 'Desafios diários de caridade e obras de misericórdia para crescer na santidade.',
      color: 'from-brown-600 to-gold-500'
    }
  ];

  const additionalFeatures = [
    {
      icon: Book,
      title: 'Biblioteca Católica',
      description: 'Acesso a orações, novenas e textos sagrados'
    },
    {
      icon: Users,
      title: 'Comunidade',
      description: 'Conecte-se com outros fiéis em oração'
    },
    {
      icon: Star,
      title: 'Liturgia Diária',
      description: 'Leituras e reflexões do dia litúrgico'
    },
    {
      icon: Shield,
      title: 'Proteção Espiritual',
      description: 'Orações de proteção e exorcismo'
    }
  ];

  return (
    <section id="funcionalidades" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-gold-100 text-gold-800 rounded-full text-sm font-semibold mb-4">
            ✨ Funcionalidades
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-brown-800 mb-6">
            Sua jornada espiritual{' '}
            <span className="text-gradient">completa</span>
          </h2>
          <p className="text-xl text-brown-600 max-w-3xl mx-auto">
            Descubra todas as ferramentas que vão transformar sua vida de oração 
            e te aproximar cada vez mais de Deus.
          </p>
        </div>

        {/* Main Features Grid - Desktop */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-16">
          {mainFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group bg-cream-50 rounded-2xl p-8 border border-cream-200 hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-display font-bold text-brown-800 mb-4">
                {feature.title}
              </h3>
              <p className="text-brown-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Main Features Carousel - Mobile */}
        <div className="md:hidden mb-16">
          <Carousel autoPlayInterval={4000} showArrows={true} showDots={true}>
            {mainFeatures.map((feature, index) => (
              <div 
                key={index}
                className="group bg-cream-50 rounded-2xl p-8 border border-cream-200 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold text-brown-800 mb-4">
                  {feature.title}
                </h3>
                <p className="text-brown-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </Carousel>
        </div>

        {/* Additional Features */}
        <div className="bg-gradient-to-br from-cream-50 to-gold-50 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-display font-bold text-brown-800 mb-4">
              E muito mais...
            </h3>
            <p className="text-brown-600 text-lg">
              Recursos adicionais para enriquecer sua experiência espiritual
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md group-hover:shadow-lg transition-shadow duration-300">
                  <feature.icon className="w-6 h-6 text-brown-600" />
                </div>
                <h4 className="font-display font-semibold text-brown-800 mb-2">
                  {feature.title}
                </h4>
                <p className="text-sm text-brown-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <Carousel autoPlayInterval={3500} showArrows={false} showDots={true}>
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="text-center group px-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md group-hover:shadow-lg transition-shadow duration-300">
                    <feature.icon className="w-6 h-6 text-brown-600" />
                  </div>
                  <h4 className="font-display font-semibold text-brown-800 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-brown-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </Carousel>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-gold-500 to-brown-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-display font-bold mb-4">
              Pronto para começar sua jornada?
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Junte-se a milhares de católicos que já transformaram sua vida espiritual
            </p>
            <a href="#inscrever" className="bg-white text-brown-800 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-cream-100 transition-colors duration-200 inline-block">
              Quero me inscrever!
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features; 